import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FullScreenPanel from "../../ui/FullScreenPanel.jsx";
import PdfButton from "../../ui/PdfButton.jsx";
import StickyActionBar from "../../ui/StickyActionBar.jsx";
import QrScanner from "../../ui/QrScanner.jsx";
import JobLoadBox, { newJobLoad, palletEntry } from "../../../features/shippingHub/JobLoadBox";
import { livePlanOrderIds } from "../../../features/shippingHub/soBoard";
import {
  filterShippingLines, groupByOrder, initialSelection, lineAssemblyPpq, lineClosedFor, loadCustomers, loadShippingLines,
  makeLineKey, queueCustomers, selectAllVisible, setAssemblySelected, setRowSelected, setShipQuantity, shipBatch,
  shipSelected, shippingManifestPath,
} from "../../../features/shippingHub/shipByJob";
import { liveSession } from "../../../features/operatorSession/session";
import { assemblyCountFromPrimarySpace } from "../../../utils/assemblyMath";
import { shopToday } from "../../../utils/shopDate";
import "../../mobile.css";
import "./byjob.css";

// Ship by job on a phone (2026-10-29): the desktop's Ready to Ship queue (pages/ShippingQueuePage.jsx renderQueueTab)
// grouped by order for a narrow screen. Every read, rule and request is features/shippingHub/shipByJob.js, the
// desktop's: lines of an order with a live packing plan are closed ("Ships from its package guide"), the yield / date
// checks, who ships = the Working-as session, POST /shipping/finalize_batch, the load box with shipping_signing.job_loads
// and its manifest offered after. Layout only.

const EMPTY_FILTERS = { product: "", salesOrder: "", customerId: "" };

export default function ShipByJobTab({ apiBase, ui, signing, board }) {
  const jobLoads = !!(signing && signing.job_loads);
  const liveSoIds = useMemo(() => livePlanOrderIds(board), [board]);
  const isClosed = useCallback((line) => lineClosedFor(liveSoIds, line), [liveSoIds]);

  const [lines, setLines] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [manifestOpen, setManifestOpen] = useState(false);
  const [manifestDate, setManifestDate] = useState("");
  const [manifestCustomer, setManifestCustomer] = useState("");
  const [shipOpen, setShipOpen] = useState(false);
  const [shipDate, setShipDate] = useState(() => shopToday());
  const [notify, setNotify] = useState(false);
  const [jobLoad, setJobLoad] = useState(() => newJobLoad());
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // {text, ok}
  const [docOffer, setDocOffer] = useState(null); // the load's manifest, just made
  const [image, setImage] = useState(null);
  const readSeq = useRef(0);

  const reload = useCallback(async () => {
    const seq = ++readSeq.current;
    setLoading(true);
    const r = await loadShippingLines(apiBase);
    if (seq !== readSeq.current) return;
    setLoading(false);
    if (r.ok) {
      setLines(r.lines);
      setSelected(initialSelection(r.lines));
      setLoadError("");
    } else {
      setLoadError(r.message);
    }
  }, [apiBase]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    let alive = true;
    loadCustomers(apiBase).then((r) => { if (alive && r.ok) setCustomers(r.customers); });
    return () => { alive = false; };
  }, [apiBase]);

  const visible = useMemo(() => filterShippingLines(lines, filters), [lines, filters]);
  const groups = useMemo(() => groupByOrder(visible), [visible]);
  const inQueue = useMemo(() => queueCustomers(lines), [lines]);
  const batch = useMemo(() => shipBatch(selected, lines, isClosed), [selected, lines, isClosed]);
  const openVisible = visible.filter((l) => !isClosed(l));
  const allTicked = openVisible.length > 0 && openVisible.every((l) => selected[makeLineKey(l.job_id, l.part_id, l.task_id)]?.selected);
  const filterCount = [filters.salesOrder.trim(), filters.customerId].filter(Boolean).length;
  const who = jobLoads ? ((liveSession() || {}).name || null) : null;
  const shipLabel = jobLoads ? `Mark loaded${who ? ` as ${who}` : ""}` : "Ship Selected";

  const setQty = (line, partId, value, consumableId = null) => {
    setSelected((prev) => setShipQuantity(prev, lines, { jobId: line.job_id, partId, taskId: line.task_id, value, consumableId }));
  };
  const tick = (line, checked) => {
    setSelected((prev) => (line.assembly_id
      ? setAssemblySelected(prev, line, checked)
      : setRowSelected(prev, makeLineKey(line.job_id, line.part_id, line.task_id), checked)));
  };

  const ship = async () => {
    if (busy) return;
    setBusy(true);
    setMessage(null); setDocOffer(null);
    const loadBox = jobLoads ? jobLoad : null;
    const r = await shipSelected({
      apiBase, ui, selected, lines, isClosed, shipDate, notifyCustomer: notify, jobLoad: loadBox,
      askPin: () => ui.identity("ship"),
    });
    setBusy(false);
    if (!r.done) return;
    setShipOpen(false);
    setNotify(false);
    if (loadBox) setJobLoad(newJobLoad());
    setMessage({ ok: true, text: r.loaded ? `Loaded on ${loadBox.pallet.label} · batch ${r.batchId}` : `Shipped · batch ${r.batchId}` });
    if (r.manifestUrl) setDocOffer({ url: r.manifestUrl, filename: `Manifest_${r.batchId}.pdf` });
    reload();
  };

  const onScanPallet = (text) => {
    setScanning(false);
    const p = palletEntry(text, true);
    if (p) setJobLoad((v) => ({ ...v, pallet: p }));
  };

  const manifestPath = manifestDate ? shippingManifestPath(manifestDate, manifestCustomer) : null;

  return (
    <div className="jq-has-action-bar" data-testid="phone-ship-byjob">
      <div className="jq-list-tools">
        <form className="jq-search" role="search" onSubmit={(e) => e.preventDefault()}>
          <i className="fas fa-search" aria-hidden="true" />
          <input type="search" enterKeyHint="search" className="form-control" aria-label="Filter by product"
            placeholder="Product ID" value={filters.product}
            onChange={(e) => setFilters((f) => ({ ...f, product: e.target.value }))} />
        </form>
        <button type="button" className="btn btn-outline-secondary jq-filter-btn" onClick={() => setFiltersOpen(true)}>
          <i className="fas fa-filter" aria-hidden="true" /> {filterCount > 0 ? filterCount : ""}
          <span className="jq-visually-hidden">Filters</span>
        </button>
        <button type="button" className="btn btn-outline-secondary jq-filter-btn" aria-label="Refresh" disabled={loading} onClick={reload}>
          <i className={`fas fa-sync-alt${loading ? " fa-spin" : ""}`} aria-hidden="true" />
        </button>
      </div>

      {message && (
        <div className={`jq-note ${message.ok ? "jq-note-info" : "jq-note-danger"} d-flex justify-content-between align-items-start`} role="status">
          <span>{message.text}</span>
          <button type="button" className="btn-close ms-2" aria-label="Dismiss" onClick={() => setMessage(null)} />
        </div>
      )}
      {docOffer && (
        <div className="jq-note jq-note-info" data-testid="phone-byjob-manifest-offer">
          <div className="mb-2">The load's manifest</div>
          <PdfButton url={docOffer.url} filename={docOffer.filename} label="Open the manifest" fetchNow className="btn btn-primary w-100" />
        </div>
      )}
      {loadError && (
        <div className="jq-note jq-note-danger" role="alert">
          Couldn't load the lines: {loadError}
          <button type="button" className="btn btn-outline-danger w-100 mt-2" onClick={reload}>Try again</button>
        </div>
      )}

      <div className="jq-card">
        <button type="button" className="btn btn-link p-0 w-100 text-start fw-semibold" style={{ minHeight: 44 }}
          aria-expanded={manifestOpen} onClick={() => setManifestOpen((v) => !v)}>
          {manifestOpen ? "▾" : "▸"} Shipping manifest for a day
        </button>
        {manifestOpen && (
          <div className="mt-2" data-testid="phone-byjob-day-manifest">
            <div className="jq-sbj-fields">
              <div>
                <label htmlFor="sbj-manifest-date">Manifest date</label>
                <input id="sbj-manifest-date" type="date" className="form-control" value={manifestDate}
                  onChange={(e) => setManifestDate(e.target.value)} />
              </div>
              <div>
                <label htmlFor="sbj-manifest-customer">Customer</label>
                <select id="sbj-manifest-customer" className="form-select" value={manifestCustomer}
                  onChange={(e) => setManifestCustomer(e.target.value)}>
                  <option value="">All customers</option>
                  {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            {manifestPath ? (
              <PdfButton key={manifestPath} url={`${apiBase}${manifestPath}`} filename={`Shipping_Manifest_${manifestDate}.pdf`}
                label="Shipping manifest" className="btn btn-outline-primary w-100" testId="phone-byjob-manifest" />
            ) : (
              <button type="button" className="btn btn-outline-primary w-100" disabled style={{ minHeight: 44 }}>Pick a date first</button>
            )}
          </div>
        )}
      </div>

      {jobLoads && (
        <div className="jq-note jq-note-info">Ticked jobs ship on a load: name the pallet when you ship them.</div>
      )}

      {loading && !lines.length ? (
        <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>
      ) : !visible.length ? (
        !loadError && <div className="jq-list-state">No lines found in the Shipping Queue.</div>
      ) : (
        <>
          <button type="button" className="btn btn-outline-secondary w-100 mb-2" style={{ minHeight: 44 }}
            disabled={!openVisible.length}
            onClick={() => setSelected((prev) => selectAllVisible(prev, visible, !allTicked, isClosed))}>
            {allTicked ? "Untick all" : `Tick all shown (${openVisible.length})`}
          </button>
          {groups.map((g) => (
            <div key={g.key} className="jq-card" data-testid="phone-byjob-order">
              <div className="jq-sbj-group-head">
                <span className="fw-bold">{g.orderNumber || "No sales order"}</span>
                <span className="jq-muted small text-end">{g.customerName}</span>
              </div>
              {g.lines.map((line) => (
                <LineRow key={line.task_id} line={line} selected={selected} closed={isClosed(line)}
                  onTick={(checked) => tick(line, checked)} onQty={setQty} onImage={setImage} />
              ))}
            </div>
          ))}
        </>
      )}

      <StickyActionBar testId="phone-byjob-bar">
        <button type="button" className="btn btn-success btn-lg" disabled={!batch.items.length}
          onClick={() => { setMessage(null); setDocOffer(null); setShipOpen(true); }}>
          {batch.items.length ? `Ship ${batch.items.length} selected…` : "Tick lines to ship"}
        </button>
      </StickyActionBar>

      <FullScreenPanel open={filtersOpen} title="Filters" onClose={() => setFiltersOpen(false)} testId="phone-byjob-filters"
        footer={(
          <div className="jq-two-buttons">
            <button type="button" className="btn btn-outline-secondary btn-lg" onClick={() => setFilters(EMPTY_FILTERS)}>Clear</button>
            <button type="button" className="btn btn-primary btn-lg" onClick={() => setFiltersOpen(false)}>Show {visible.length}</button>
          </div>
        )}>
        <div className="jq-filter-field">
          <label htmlFor="sbj-filter-so">Sales order</label>
          <input id="sbj-filter-so" type="text" className="form-control" style={{ minHeight: 44 }} placeholder="SO-100…"
            value={filters.salesOrder} onChange={(e) => setFilters((f) => ({ ...f, salesOrder: e.target.value }))} />
        </div>
        <div className="jq-filter-field">
          <label htmlFor="sbj-filter-customer">Customer</label>
          <select id="sbj-filter-customer" className="form-select" style={{ minHeight: 44 }} value={filters.customerId}
            onChange={(e) => setFilters((f) => ({ ...f, customerId: e.target.value }))}>
            <option value="">All customers</option>
            {inQueue.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
          </select>
        </div>
      </FullScreenPanel>

      <FullScreenPanel open={shipOpen} title={jobLoads ? "Load the ticked jobs" : "Ship the ticked lines"}
        onClose={busy ? undefined : () => setShipOpen(false)} testId="phone-byjob-ship"
        footer={(
          <div className="jq-two-buttons">
            <button type="button" className="btn btn-outline-secondary btn-lg" disabled={busy} onClick={() => setShipOpen(false)}>Cancel</button>
            <button type="button" className="btn btn-success btn-lg" disabled={busy} onClick={ship}>
              {busy && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}{shipLabel}
            </button>
          </div>
        )}>
        <div className="jq-block">
          <h3>Shipping</h3>
          {batch.items.map((it) => {
            const line = lines.find((l) => l.task_id === it.task_id) || {};
            const ppq = lineAssemblyPpq(line);
            const n = ppq > 1 ? it.final_shipped_qty / ppq : it.final_shipped_qty;
            return (
              <div key={it.task_id} className="jq-task-line">
                <div className="jq-task-line-main"><strong>{line.product_id}</strong> <span className="jq-muted">Job {line.master_job_num}</span></div>
                <div className="fw-bold">{n}{ppq > 1 ? " assemblies" : ""}</div>
              </div>
            );
          })}
        </div>
        <div className="jq-filter-field">
          <label htmlFor="sbj-ship-date">Shipment date</label>
          <input id="sbj-ship-date" type="date" className="form-control" style={{ minHeight: 44 }} value={shipDate}
            onChange={(e) => setShipDate(e.target.value)} />
        </div>
        <div className="form-check form-switch jq-sbj-switch mb-3">
          <input className="form-check-input" type="checkbox" id="sbj-notify" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          <label className="form-check-label" htmlFor="sbj-notify">Email Customer Manifest</label>
        </div>
        {jobLoads && (
          <>
            <JobLoadBox apiBase={apiBase} value={jobLoad} onChange={setJobLoad} />
            {!jobLoad.pallet && (
              <button type="button" className="btn btn-outline-primary w-100 mb-3" style={{ minHeight: 48 }} onClick={() => setScanning(true)}>
                <i className="fas fa-qrcode me-2" aria-hidden="true" />Scan the pallet's beacon or label
              </button>
            )}
          </>
        )}
      </FullScreenPanel>

      <FullScreenPanel open={scanning} title="Scan the pallet" onClose={() => setScanning(false)} testId="phone-byjob-scan">
        {scanning && <QrScanner onScan={onScanPallet} />}
      </FullScreenPanel>

      <FullScreenPanel open={!!image} title="Part" onClose={() => setImage(null)} testId="phone-byjob-image">
        {image && <img src={image} alt="Part" className="jq-sbj-image" />}
      </FullScreenPanel>
    </div>
  );
}

/** One queue line: tick, what it is, ordered / defects, the ship qty; an assembly with its components below. */
function LineRow({ line, selected, closed, onTick, onQty, onImage }) {
  const key = makeLineKey(line.job_id, line.part_id, line.task_id);
  const sel = selected[key] || { selected: false, finalShipped: 0 };
  const ppq = lineAssemblyPpq(line);
  const ordered = line.assembly_id ? assemblyCountFromPrimarySpace(line.quantity_ordered, ppq) : line.quantity_ordered;
  const label = `Ship ${line.product_id} (job ${line.master_job_num})`;
  return (
    <div className={`jq-sbj-line${closed ? " jq-sbj-closed" : ""}`} data-testid="phone-byjob-line">
      <div className="jq-sbj-line-top">
        <label className="jq-sbj-check">
          <input type="checkbox" aria-label={label} checked={sel.selected && !closed} disabled={closed}
            onChange={(e) => onTick(e.target.checked)} />
        </label>
        <div className="jq-sbj-body">
          <div className="fw-bold">{line.product_id}{line.assembly_id ? <span className="jq-tag ms-2">Assembly</span> : null}</div>
          <div className="small jq-muted">Job {line.master_job_num} · {line.description || "N/A"}</div>
          <div className="small">Ordered {ordered != null ? ordered : 0} · Defects {line.defects || 0}</div>
          {closed ? (
            <div className="small fw-semibold mt-1" data-testid="phone-ships-from-guide">Ships from its package guide</div>
          ) : (
            <div className="jq-sbj-qty">
              <label htmlFor={`sbj-qty-${line.task_id}`} className="small fw-semibold">Ship qty{line.assembly_id && ppq > 1 ? " (assemblies)" : ""}</label>
              <input id={`sbj-qty-${line.task_id}`} type="number" inputMode="numeric" min="0" className="form-control"
                value={sel.finalShipped} onChange={(e) => onQty(line, line.part_id, e.target.value)} />
            </div>
          )}
        </div>
        {line.image_path && (
          <button type="button" className="jq-sbj-thumb" aria-label={`Picture of ${line.product_id}`} onClick={() => onImage(line.image_path)}>
            <img src={line.image_path} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </button>
        )}
      </div>
      {(line.assembly_components || []).filter((c) => !c.is_primary).map((comp) => {
        const ck = makeLineKey(line.job_id, comp.part_id, line.task_id, comp.consumable_id);
        const cs = selected[ck] || { finalShipped: 0 };
        return (
          <div key={ck} className="jq-sbj-comp">
            <strong>{comp.product_id}</strong> {comp.description ? <span className="jq-muted">· {comp.description}</span> : null}
            <div>Ordered {comp.quantity_ordered || 0} · Defects {comp.defects || 0} · Ships {cs.finalShipped}</div>
          </div>
        );
      })}
    </div>
  );
}
